/* NAWA premium — gedrag voor de nawa-p-* secties.
   - Hero: rustige crossfade met voortgangsbalk en pauzeknop.
   - Rails: schuiven automatisch één kaart op, langzaam (slow-mo), pauzeren bij hover/focus/aanraking
     en wanneer ze buiten beeld zijn. Bij "minder beweging" staat alles stil.
   - Winkelwagen: dezelfde inline-cart als de rest van het thema (window.refreshCart). */
(() => {
  // Eén keer starten, ook als het script per ongeluk twee keer geladen wordt (anders dubbele add-to-cart).
  if (window.__npBooted) return;
  window.__npBooted = true;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const still = () => reduce.matches;

  /* ---------- Hero ---------- */
  const initHero = (root) => {
    if (!root || root.dataset.npReady) return;
    root.dataset.npReady = '1';
    const slides = [...root.querySelectorAll('[data-np-slide]')];
    const dots = [...root.querySelectorAll('[data-np-dot]')];
    const toggle = root.querySelector('[data-np-toggle]');
    const interval = parseInt(root.dataset.interval, 10) || 7000;
    root.style.setProperty('--np-interval', interval + 'ms');
    let i = 0, timer = null, userPaused = false;

    const show = (n) => {
      i = (n + slides.length) % slides.length;
      slides.forEach((s, k) => {
        const on = k === i;
        s.classList.toggle('is-active', on);
        s.setAttribute('aria-hidden', on ? 'false' : 'true');
        s.querySelectorAll('a, button').forEach((el) => { el.tabIndex = on ? 0 : -1; });
      });
      dots.forEach((d, k) => {
        d.classList.remove('is-active');
        d.setAttribute('aria-current', k === i ? 'true' : 'false');
      });
      // herstart de voortgangsanimatie
      if (dots[i]) { void dots[i].offsetWidth; dots[i].classList.add('is-active'); }
    };
    const stop = () => { clearTimeout(timer); timer = null; root.classList.add('is-paused'); };
    const play = () => {
      if (userPaused || still() || slides.length < 2) return stop();
      root.classList.remove('is-paused');
      clearTimeout(timer);
      timer = setTimeout(() => { show(i + 1); play(); }, interval);
    };

    dots.forEach((d, k) => d.addEventListener('click', () => { show(k); play(); }));
    if (toggle) {
      toggle.addEventListener('click', () => {
        userPaused = !userPaused;
        toggle.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
        toggle.setAttribute('aria-label', userPaused ? toggle.dataset.labelPlay : toggle.dataset.labelPause);
        userPaused ? stop() : play();
      });
      if (slides.length < 2 || still()) toggle.hidden = true;
    }
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', (e) => { if (!root.contains(e.relatedTarget)) play(); });
    new IntersectionObserver(([e]) => (e.isIntersecting ? play() : stop()), { threshold: 0.2 }).observe(root);
    show(0);
    play();
  };

  /* ---------- Rails (slow-mo autoplay) ---------- */
  const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  const initRail = (rail) => {
    if (!rail || rail.dataset.npReady) return;
    rail.dataset.npReady = '1';
    const track = rail.querySelector('[data-np-track]');
    if (!track) return;
    const scope = rail.closest('[data-np-rail-scope]') || rail;
    const prev = scope.querySelector('[data-np-prev]');
    const next = scope.querySelector('[data-np-next]');
    const auto = rail.dataset.auto !== 'false';
    const delay = parseInt(rail.dataset.delay, 10) || 4200;
    const glide = parseInt(rail.dataset.glide, 10) || 1400;
    let timer = null, hovering = false, visible = false, anim = null;

    const step = () => {
      const first = track.children[0];
      if (!first) return 300;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return first.getBoundingClientRect().width + gap;
    };
    const max = () => Math.max(0, track.scrollWidth - track.clientWidth);
    const sync = () => {
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max() - 2;
    };
    const glideTo = (x, dur) => {
      cancelAnimationFrame(anim);
      const from = track.scrollLeft, to = Math.max(0, Math.min(max(), x));
      if (still() || dur === 0) { track.scrollLeft = to; sync(); return; }
      rail.classList.add('is-animating');
      const t0 = performance.now();
      const tick = (now) => {
        const p = Math.min(1, (now - t0) / dur);
        track.scrollLeft = from + (to - from) * easeInOut(p);
        if (p < 1) anim = requestAnimationFrame(tick);
        else { rail.classList.remove('is-animating'); sync(); }
      };
      anim = requestAnimationFrame(tick);
    };
    const advance = () => {
      if (track.scrollLeft >= max() - 2) glideTo(0, glide * 1.6);
      else glideTo(track.scrollLeft + step(), glide);
    };
    const stop = () => { clearTimeout(timer); timer = null; };
    const play = () => {
      stop();
      if (!auto || still() || hovering || !visible || max() < 4) return;
      timer = setTimeout(() => { advance(); play(); }, delay);
    };

    prev && prev.addEventListener('click', () => { glideTo(track.scrollLeft - step(), 700); play(); });
    next && next.addEventListener('click', () => { glideTo(track.scrollLeft + step(), 700); play(); });
    track.addEventListener('scroll', sync, { passive: true });
    rail.addEventListener('mouseenter', () => { hovering = true; stop(); });
    rail.addEventListener('mouseleave', () => { hovering = false; play(); });
    rail.addEventListener('focusin', () => { hovering = true; stop(); });
    rail.addEventListener('focusout', (e) => { if (!rail.contains(e.relatedTarget)) { hovering = false; play(); } });
    // Aanraking = de klant neemt het over; daarna pas na een rustpauze weer verder.
    track.addEventListener('touchstart', () => { stop(); cancelAnimationFrame(anim); rail.classList.remove('is-animating'); }, { passive: true });
    track.addEventListener('touchend', () => { setTimeout(play, 6000); }, { passive: true });
    new IntersectionObserver(([e]) => { visible = e.isIntersecting; play(); }, { threshold: 0.35 }).observe(rail);
    window.addEventListener('resize', sync);
    rail._npPlay = play;
    rail._npReset = () => { glideTo(0, 0); play(); };
    sync();
  };

  /* ---------- Tabs (producten) ---------- */
  const initTabs = (root) => {
    if (!root || root.dataset.npTabsReady) return;
    root.dataset.npTabsReady = '1';
    const tabs = [...root.querySelectorAll('[role="tab"]')];
    const select = (tab) => {
      tabs.forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        const panel = root.querySelector('#' + t.getAttribute('aria-controls'));
        if (panel) {
          panel.hidden = !on;
          if (on) {
            const tpl = panel.querySelector(':scope > template');
            if (tpl) { panel.appendChild(tpl.content.cloneNode(true)); tpl.remove(); }
            panel.querySelectorAll('[data-np-rail]').forEach((r) => { initRail(r); r._npReset && r._npReset(); });
          }
        }
      });
    };
    tabs.forEach((t, k) => {
      t.addEventListener('click', () => select(t));
      t.addEventListener('keydown', (e) => {
        const map = { ArrowRight: k + 1, ArrowLeft: k - 1, Home: 0, End: tabs.length - 1 };
        if (!(e.key in map)) return;
        e.preventDefault();
        const n = tabs[(map[e.key] + tabs.length) % tabs.length];
        n.focus(); select(n);
      });
    });
  };

  /* ---------- Marquee pauzeknop ---------- */
  const initMarquee = (root) => {
    const btn = root.querySelector('[data-np-marquee-toggle]');
    const box = root.querySelector('.np-marquee');
    if (!btn || !box || btn.dataset.npReady) return;
    btn.dataset.npReady = '1';
    if (still()) { btn.hidden = true; return; }
    btn.addEventListener('click', () => {
      const paused = box.classList.toggle('is-paused');
      btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
      btn.setAttribute('aria-label', paused ? btn.dataset.labelPlay : btn.dataset.labelPause);
    });
  };

  /* ---------- Winkelwagen ---------- */
  const openCart = async () => {
    try { if (typeof window.refreshCart === 'function') await window.refreshCart(); } catch (e) { /* cart ververst bij openen */ }
    const cart = document.querySelector('.inline-cart');
    const overlay = document.querySelector('.inline-cart__page-overlay');
    if (cart) cart.classList.add('active');
    if (overlay) overlay.classList.add('active');
    if (cart) document.body.classList.add('body--no-scroll');
    else window.location.href = '/cart';
  };
  const addItems = async (items, btn) => {
    if (!items.length) return;
    btn && btn.setAttribute('aria-busy', 'true');
    try {
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });
      if (!r.ok) throw new Error('add failed');
      if (btn) {
        btn.classList.add('is-added');
        btn.setAttribute('data-np-added', '');
        setTimeout(() => { btn.classList.remove('is-added'); btn.removeAttribute('data-np-added'); }, 2200);
      }
      await openCart();
    } catch (e) {
      // Terugval: de standaard Shopify-route voegt toe en opent de winkelwagenpagina.
      // Eén product: /cart/add; meerdere: officiële cart-permalink /cart/id:aantal,id:aantal
      window.location.href = items.length === 1
        ? '/cart/add?id=' + items[0].id + '&quantity=' + (items[0].quantity || 1)
        : '/cart/' + items.map((it) => it.id + ':' + (it.quantity || 1)).join(',');
      return;
    } finally {
      btn && btn.removeAttribute('aria-busy');
    }
  };
  document.addEventListener('click', (e) => {
    const one = e.target.closest('[data-np-add]');
    if (one) { e.preventDefault(); addItems([{ id: Number(one.dataset.npAdd), quantity: 1 }], one); return; }
    const bundle = e.target.closest('[data-np-add-routine]');
    if (bundle) {
      e.preventDefault();
      const box = bundle.closest('[data-np-routine]');
      const items = [...box.querySelectorAll('input[data-variant]:checked')].map((c) => ({ id: Number(c.dataset.variant), quantity: 1 }));
      addItems(items, bundle);
    }
  });

  /* Routine: totaal live bijwerken */
  const initRoutine = (box) => {
    if (!box || box.dataset.npReady) return;
    box.dataset.npReady = '1';
    const fmt = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: box.dataset.currency || 'GBP' }).format(cents / 100);
    const freeAt = parseInt(box.dataset.freeAt, 10) || 0;
    const sum = box.querySelector('[data-np-sum]');
    const was = box.querySelector('[data-np-was]');
    const btn = box.querySelector('[data-np-add-routine]');
    const label = box.querySelector('[data-np-count]');
    const update = () => {
      const picked = [...box.querySelectorAll('input[data-variant]:checked')];
      const prices = picked.map((c) => parseInt(c.dataset.price, 10)).sort((a, b) => a - b);
      let total = prices.reduce((a, b) => a + b, 0);
      // "Buy 3, pay for 2": bij drie stuks valt de goedkoopste weg (zelfde regel als de cart).
      const deal = box.dataset.deal === 'true' && prices.length >= 3;
      const pay = deal ? total - prices[0] : total;
      if (sum) sum.textContent = fmt(pay);
      if (was) { was.textContent = deal ? fmt(total) : ''; was.hidden = !deal; }
      if (label) label.textContent = picked.length;
      if (btn) btn.disabled = picked.length === 0;
      const note = box.querySelector('[data-np-ship]');
      if (note && freeAt) note.hidden = pay < freeAt;
    };
    box.addEventListener('change', update);
    update();
  };


  /* ---------- Video's (hero + banner) ----------
     Laadt pas de juiste bron (desktop of mobiel) als de video in beeld komt; speelt niet bij "minder beweging". */
  const initVideos = (root) => {
    if (!root || root.dataset.npVReady) return;
    root.dataset.npVReady = '1';
    const vids = [...root.querySelectorAll('video[data-src]')];
    const toggle = root.querySelector('[data-np-vtoggle]');
    let userPaused = false;
    const active = () => vids.find((v) => v.offsetParent !== null) || vids[0];
    const load = (v) => { if (v && !v.src) { v.src = v.dataset.src; } };
    const play = () => {
      const v = active();
      if (!v || userPaused || still()) return;
      load(v);
      const p = v.play(); if (p && p.catch) p.catch(() => {});
    };
    const pause = () => vids.forEach((v) => v.pause());
    if (toggle) {
      if (still()) toggle.hidden = true;
      toggle.addEventListener('click', () => {
        userPaused = !userPaused;
        toggle.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
        toggle.setAttribute('aria-label', userPaused ? toggle.dataset.labelPlay : toggle.dataset.labelPause);
        userPaused ? pause() : play();
      });
    }
    new IntersectionObserver(([e]) => (e.isIntersecting ? play() : pause()), { threshold: 0.15 }).observe(root);
    window.matchMedia('(max-width: 749px)').addEventListener('change', () => { pause(); play(); });
  };


  /* ---------- Plakkende koopbalk (PDP) ---------- */
  const initSticky = (bar) => {
    if (!bar || bar.dataset.npReady) return;
    bar.dataset.npReady = '1';
    const anchor = document.querySelector('.add-to-cart-button--primary-trigger') || document.querySelector('form[action*="/cart/add"] [type="submit"]');
    const footer = document.querySelector('footer, .novo-footer, [class*="footer"]');
    const themeSticky = document.querySelector('.sticky-add-to-cart');
    let pastBuy = false, nearFooter = false;
    const themeVisible = () => themeSticky && themeSticky.offsetParent !== null && themeSticky.getBoundingClientRect().height > 0 && getComputedStyle(themeSticky).visibility !== 'hidden';
    const update = () => {
      const show = pastBuy && !nearFooter && !themeVisible();
      if (show && bar.hidden) { bar.hidden = false; requestAnimationFrame(() => bar.classList.add('is-in')); }
      if (!show && !bar.hidden) { bar.classList.remove('is-in'); setTimeout(() => { if (!bar.classList.contains('is-in')) bar.hidden = true; }, still() ? 0 : 300); }
    };
    if (anchor) new IntersectionObserver(([e]) => { pastBuy = !e.isIntersecting && e.boundingClientRect.top < 0; update(); }).observe(anchor);
    if (footer) new IntersectionObserver(([e]) => { nearFooter = e.isIntersecting; update(); }, { rootMargin: '0px 0px -10% 0px' }).observe(footer);
  };

  /* ---------- Eén verfijnde opening van de hero ---------- */
  const introHero = (root) => {
    if (!root || root.dataset.npIntro || still()) return;
    root.dataset.npIntro = '1';
    root.classList.add('np-intro');
    requestAnimationFrame(() => requestAnimationFrame(() => root.classList.add('np-intro--go')));
  };


  /* ---------- Geschatte bezorgdatum (werkdagen, weekend overgeslagen) ---------- */
  const initEta = (el) => {
    if (!el || el.dataset.npReady) return;
    el.dataset.npReady = '1';
    const addWorkdays = (d, n) => { const r = new Date(d); while (n > 0) { r.setDate(r.getDate() + 1); if (r.getDay() !== 0 && r.getDay() !== 6) n--; } return r; };
    const today = new Date();
    const dispatch = addWorkdays(today, parseInt(el.dataset.dispatch, 10) || 0);
    const from = addWorkdays(dispatch, parseInt(el.dataset.min, 10) || 1);
    const to = addWorkdays(dispatch, parseInt(el.dataset.max, 10) || 3);
    const fmt = (d) => d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
    const out = el.querySelector('[data-np-eta-text]');
    if (out) out.textContent = fmt(from) + ' – ' + fmt(to);
  };

  const boot = (scope = document) => {
    scope.querySelectorAll('[data-np-hero]').forEach(initHero);
    scope.querySelectorAll('[data-np-vhero], [data-np-vbanner], [data-np-ugc]').forEach(initVideos);
    scope.querySelectorAll('[data-np-tabs]').forEach(initTabs);
    scope.querySelectorAll('[data-np-rail]').forEach((r) => { if (!r.closest('[hidden]')) initRail(r); });
    scope.querySelectorAll('[data-np-reviews]').forEach(initMarquee);
    scope.querySelectorAll('[data-np-routine]').forEach(initRoutine);
    scope.querySelectorAll('[data-np-sticky]').forEach(initSticky);
    scope.querySelectorAll('[data-np-eta]').forEach(initEta);
    scope.querySelectorAll('[data-np-vhero]').forEach(introHero);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
